import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { RoomMatch } from "../../../api/room";
import { movieTitleHandler } from "../../../utils/helpers";
import styles from "./RoomMatchesSidebar.module.scss";
import HeartIcon from "../../../components/icons/room/HeartIcon";
import OverlayRatingIcon from "../../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../../components/icons/searchIcons/OverlayTomatoRatingIcon";
import LikeButton from "../../../components/shared/LikeButton";
import useLikeClick from "../../../hooks/useLikeClick";
import { setVoteCount } from "../../../store/slices/lists.slice";
import useInterfaceContext from "../../../components/providers/interface";
import { genresData } from "../../../constants/structure";

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

interface RoomMatchesSidebarProps {
  matches: RoomMatch[];
  isOpen: boolean;
  onClose: () => void;
  onSelectMatch: (matchId: number) => void;
}

const RoomMatchesSidebar: React.FC<RoomMatchesSidebarProps> = ({
  matches,
  isOpen,
  onSelectMatch,
}) => {
  const { t, i18n } = useTranslation();
  const { likesList, likeHandler } = useLikeClick();
  const { isMobile } = useInterfaceContext();
  const dispatch = useDispatch();
  const likedIds = Array.isArray(likesList?.data?.data) ? likesList.data.data : [];

  if (!isOpen) return null;

  const updateVoteCount = (id: any, key: any, type: any) => {
      dispatch(setVoteCount({ id, key, type }));
  };

  const formatLikesCount = (count: number) => {
    if (count < 1000) return `${count}`;

    const compactCount = Math.floor(count / 100) / 10;
    return `${Number.isInteger(compactCount) ? compactCount.toFixed(0) : compactCount.toFixed(1)}k`;
  };


  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <HeartIcon/>
        <span>{t("room_common_choice", "Common Choice")}</span>
      </div>

      <div className={styles.matchList}>
        {matches.map((match) => {
          const detail = match.movie_detail;
          const title = detail ? movieTitleHandler(i18n.language, detail) || detail.title : match.movie_title;
          const posterPath = detail?.poster_path || match.movie_poster_path;
          const year = detail?.release_date?.slice(0, 4);
          const imdbRating = detail ? (detail.imdb_rating?.toFixed(1) || detail.vote_average?.toFixed(1)) : null;
          const tomatoRating = detail ? (detail.rotten_tomatoes_rating?.toFixed(0)) : null;
          const likesCount = detail?.likes_count ?? 0;
          const liked = likedIds.includes(match.movie_id);

          const genreList = detail?.genres
            ? JSON.parse(detail.genres)
              .slice(0, 2)
              .map((id: number) => genresData.find((genre) => genre.id === id)?.name)
              .filter(Boolean)
              .map((name: string) => t(name))
            : [];

          return (
            <div
              key={match.id}
              className={`${styles.matchCard} ${match.is_selected ? styles.matchCardSelected : ""}`}
              onClick={() => !match.is_selected && onSelectMatch(match.id)}
            >
              <div className={styles.matchPoster}>
                {posterPath ? (
                  <img src={`${IMG_BASE}${posterPath}`} alt={title} />
                ) : (
                  <div className={styles.matchPosterPlaceholder}>{title?.charAt(0)}</div>
                )}
              </div>
              <div className={styles.matchInfo}>
                <div className={styles.matchTitle}>{title}</div>
                <div className={styles.matchMeta}>
                  {year && <span className={styles.matchYear}>{year}</span>}
                  {genreList.length > 0 && (
                    <div className={styles.matchGenres}>
                      {genreList.join(", ")}
                    </div>
                  )}
                </div>

                <div className={styles.matchActionsWrapper}>
                  {imdbRating && (
                    <div className={styles.ratingItem}>
                      <OverlayRatingIcon width={10} height={10}/>
                      <p className={styles.rating}>{imdbRating}</p>
                    </div>
                  )}
                  {tomatoRating && (
                    <div className={styles.ratingItem}>
                      <OverlayTomatoRatingIcon width={10} height={10}/>
                      <p className={styles.rating}>{tomatoRating}</p>
                    </div>
                  )}
                  <LikeButton
                    clickHandler={likeHandler}
                    direction="row"
                    isMobile={isMobile}
                    count={formatLikesCount(likesCount)}
                    updateFieldLocal={updateVoteCount}
                    liked={liked}
                    item={match}
                    iconWidth={10}
                    iconHeight={10}
                  />
                </div>

              </div>
            </div>
          );
        })}

        {matches.length === 0 && (
          <div className={styles.emptyState}>
            {t("room_no_matches_yet", "No matches yet")}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomMatchesSidebar;
